import React, { useState, useEffect, forwardRef, useRef } from "react";
import { socket } from "./socket";
import LobbyArea from "./components/updated-ui/LobbyArea";
import GameArea from "./components/updated-ui/GameArea";
import LandingPage from "./components/updated-ui/LandingPage";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  Slide,
  Zoom,
  IconButton,
  List,
  Button,
  ListItem,
  Tooltip,
  alpha,
  Avatar,
} from "@mui/material";
import CustomSnackbar from "./components/CustomSnackbar";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import GavelIcon from "@mui/icons-material/Gavel";
import { v4 as uuidv4 } from "uuid";
import GameRules from "./components/GameRules";
import CustomNavbar from "./components/CustomNavbar";
import SplashScreen from "./components/updated-ui/SplashScreen";
import AlertDialog from "./components/updated-ui/AlertDialog";
import FeedbackDialog from "./components/FeedbackDialog";
import { AnimatePresence, motion } from "framer-motion";

const SlideTransition = forwardRef(function SlideTransition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ZoomTransition = forwardRef(function ZoomTransition(props, ref) {
  return <Zoom ref={ref} {...props} />;
});

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.25 } },
};

function App() {
  const [isConnected, setIsConnected] = useState(socket.connected);
  const [roomCode, setRoomCode] = useState(null);
  const [player, setPlayer] = useState(null);
  const [roomState, setRoomState] = useState(null);
  const [systemMessage, setSystemMessage] = useState("");
  const [gameCompleted, setGameCompleted] = useState(false);
  const [finalRoomInfo, setFinalRoomInfo] = useState(null);
  const [openRules, setOpenRules] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    title: "",
    content: "",
    type: "info",
    onConfirm: null,
  });
  const [rematchRounds, setRematchRounds] = useState(5);
  const [gameKey, setGameKey] = useState(0); // incremented on rematch to force GameArea remount
  const [showLanding, setShowLanding] = useState(!localStorage.getItem("roomCode"));
  const [openFeedback, setOpenFeedback] = useState(false);

  const showAlert = (title, content, type = "info", onConfirm = null) => {
    setAlertInfo({ open: true, title, content, type, onConfirm });
  };
  const handleCloseAlert = () => setAlertInfo((p) => ({ ...p, open: false }));

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const playerIdRef = useRef(null);

  // === Socket setup (unchanged logic) ===
  useEffect(() => {
    if (!playerIdRef.current) {
      let storedPlayerId = localStorage.getItem("playerId");
      if (!storedPlayerId) {
        storedPlayerId = uuidv4();
        localStorage.setItem("playerId", storedPlayerId);
      }
      playerIdRef.current = storedPlayerId;
      socket.playerId = storedPlayerId;
    }

    const savedRoomCode = localStorage.getItem("roomCode");
    if (savedRoomCode) {
      socket.emit("reconnectPlayer", {
        roomCode: savedRoomCode,
        playerId: playerIdRef.current,
      });
    }

    const onConnect = () => {
      setIsConnected(true);
      // Auto-reconnect to room on every socket connect (handles refresh, tab close, network drop)
      const savedRoom = localStorage.getItem("roomCode");
      if (savedRoom && playerIdRef.current) {
        socket.emit("reconnectPlayer", { roomCode: savedRoom, playerId: playerIdRef.current });
      }
    };
    const onDisconnect = () => { setIsConnected(false); setSystemMessage("Disconnected. Trying to reconnect..."); };
    const onRoomCreated = ({ roomCode, player }) => { setRoomCode(roomCode); setPlayer(player); localStorage.setItem("roomCode", roomCode); };
    const onRoomJoined = ({ roomCode, player }) => { setRoomCode(roomCode); setPlayer(player); localStorage.setItem("roomCode", roomCode); };
    const onRoomUpdate = (updatedRoom) => {
      setRoomState(updatedRoom);
      if (player) {
        const self = updatedRoom.players.find((p) => p.playerId === player.playerId || p.id === player.id);
        if (self) setPlayer((prev) => ({ ...prev, ...self }));
      }
    };
    const onReconnectSuccess = ({ roomState }) => {
      setRoomCode(roomState.roomCode);
      const self = roomState.players.find((p) => p.playerId === playerIdRef.current);
      if (self) { setPlayer(self); setRoomState(roomState); }
      else { localStorage.removeItem("roomCode"); setRoomCode(null); setPlayer(null); setRoomState(null); setSystemMessage("Failed to reconnect."); }
    };
    const onReconnectFailed = (message) => { localStorage.removeItem("roomCode"); setRoomCode(null); setPlayer(null); setRoomState(null); setSystemMessage(`Reconnection failed: ${message}`); };
    const onGameCompleted = (finalRoomState) => { setFinalRoomInfo(finalRoomState); setGameCompleted(true); };
    const onGameEnded = (message = "Game has ended.") => {
      setSystemMessage(message);
      setTimeout(() => { localStorage.removeItem("roomCode"); setRoomCode(null); setPlayer(null); setRoomState(null); setGameCompleted(false); setFinalRoomInfo(null); }, 3000);
    };
    const onRematchStarted = () => {
      // Close results dialog, reset GameArea completely (clears roundResult, timers, etc.)
      setGameCompleted(false);
      setFinalRoomInfo(null);
      setGameKey((k) => k + 1); // forces GameArea to remount — clears all stale local state
    };
    const onYouAreHost = () => { setPlayer((p) => ({ ...p, isHost: true })); setSystemMessage("You are now the host!"); };
    const onError = (msg) => setSystemMessage(`Error: ${msg}`);
    const onRoomNotFound = () => { setSystemMessage("Room not found."); localStorage.removeItem("roomCode"); };
    const onRoomFull = () => setSystemMessage("Room is full.");

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("roomCreated", onRoomCreated);
    socket.on("roomJoined", onRoomJoined);
    socket.on("roomUpdate", onRoomUpdate);
    socket.on("reconnectSuccess", onReconnectSuccess);
    socket.on("reconnectFailed", onReconnectFailed);
    socket.on("gameCompleted", onGameCompleted);
    socket.on("gameEnded", onGameEnded);
    socket.on("rematchStarted", onRematchStarted);
    socket.on("youAreHost", onYouAreHost);
    socket.on("yourRole", ({ role }) => setPlayer((p) => ({ ...p, role })));
    socket.on("systemMessage", ({ text }) => setSystemMessage(text));
    socket.on("error", onError);
    socket.on("roomNotFound", onRoomNotFound);
    socket.on("roomFull", onRoomFull);
    socket.on("chatMessage", (chatMsg) => {
      // Use client-side timestamp for display accuracy
      chatMsg.timestamp = new Date().toISOString();
      setRoomState((prev) => {
        if (!prev) return prev;
        return { ...prev, messages: [...(prev.messages || []), chatMsg] };
      });
    });
    socket.on("syncTimer", () => { });

    if (!socket.connected) socket.connect();

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("roomCreated", onRoomCreated);
      socket.off("roomJoined", onRoomJoined);
      socket.off("roomUpdate", onRoomUpdate);
      socket.off("reconnectSuccess", onReconnectSuccess);
      socket.off("reconnectFailed", onReconnectFailed);
      socket.off("gameCompleted", onGameCompleted);
      socket.off("gameEnded", onGameEnded);
      socket.off("rematchStarted", onRematchStarted);
      socket.off("youAreHost", onYouAreHost);
      socket.off("yourRole");
      socket.off("systemMessage");
      socket.off("error");
      socket.off("roomNotFound");
      socket.off("roomFull");
      socket.off("chatMessage");
      socket.off("syncTimer");
    };
  }, []);

  const handleCloseDialog = () => {
    if (player?.isHost) socket.emit("endGame", { roomCode });
    setGameCompleted(false);
    localStorage.removeItem("roomCode");
    setRoomCode(null); setPlayer(null); setRoomState(null); setFinalRoomInfo(null);
  };

  const currentRound = roomState?.currentRound ?? 0;
  const currentRole = roomState?.players?.find((p) => p.id === player?.id)?.role || player?.role;

  const handleCopyRoomCode = async () => {
    try { await navigator.clipboard.writeText(roomCode); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch (err) { console.error("Failed to copy:", err); }
  };

  const handleLeaveRoom = () => {
    showAlert("Leave Room", "Are you sure you want to leave?", "warning", () => {
      socket.emit("leaveRoom");
      localStorage.removeItem("roomCode");
      setRoomCode(null); setPlayer(null); setRoomState(null);
      setShowLanding(true);
    });
  };

  if (showSplash) return <SplashScreen onComplete={() => setShowSplash(false)} />;

  const isInGame = roomCode && player;

  // Show landing page when user is not in a game session
  if (showLanding && !isInGame) {
    return <LandingPage onPlay={() => setShowLanding(false)} />;
  }

  // Medal colors for final results
  const medalColors = ["#FFD700", "#C0C0C0", "#CD7F32"];

  return (
    <Box
      sx={{
        height: "100dvh",
        background: theme.palette.gradient.bg,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: "background 0.3s ease",
      }}
    >
      <CustomNavbar
        leftContent={
          <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 0.5, opacity: 0.85 }}>
            {isInGame && (
              <>
                <Tooltip title={copied ? "Copied!" : "Copy code"} arrow>
                  <Button
                    onClick={handleCopyRoomCode}
                    variant="text"
                    size="small"
                    sx={{ minWidth: 0, px: 0.5, py: 0, fontWeight: 700, fontSize: "0.8rem", letterSpacing: 1 }}
                  >
                    {roomCode}
                    <ContentCopyIcon sx={{ ml: 0.5, fontSize: "0.7rem", opacity: 0.6 }} />
                  </Button>
                </Tooltip>
                <Box component="span" sx={{ mx: 0.5, opacity: 0.4 }}>|</Box>
                <span>R{currentRound}/{roomState?.totalRounds}</span>
              </>
            )}
          </Typography>
        }
        centerContent={
          roomState?.status === "playing" && player ? (
            <Typography variant="subtitle1" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 0.5 }}>
              {player.username}
              {currentRole && currentRole !== "None" && (
                <Box
                  component="span"
                  sx={{
                    ml: 0.5,
                    px: 1, py: 0.25,
                    borderRadius: 1,
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    background: theme.palette.gradient.brand,
                    color: "#fff",
                  }}
                >
                  {currentRole}
                </Box>
              )}
            </Typography>
          ) : (
            /* Hide RMCS title on mobile when in a room — save space */
            !isMobile || !isInGame ? (
              <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: 1 }}>
                RMCS
              </Typography>
            ) : null
          )
        }
        rightContent={
          /* Only the online dot stays inline in the header */
          <Box
            sx={{
              width: 8, height: 8, borderRadius: "50%",
              backgroundColor: isConnected ? "success.main" : "error.main",
              boxShadow: isConnected
                ? `0 0 6px ${theme.palette.success.main}`
                : `0 0 6px ${theme.palette.error.main}`,
            }}
          />
        }
        menuItems={[
          ...(isInGame ? [{
            label: "Leave Room",
            icon: <ExitToAppIcon fontSize="small" />,
            color: theme.palette.error.main,
            onClick: handleLeaveRoom,
          }] : []),
          {
            label: "Rules",
            icon: <GavelIcon fontSize="small" />,
            onClick: () => setOpenRules(true),
          },
          {
            label: "Feedback",
            icon: <span style={{ fontSize: 14 }}>💬</span>,
            onClick: () => setOpenFeedback(true),
          },
        ]}
      />

      {/* Rules Dialog */}
      <Dialog open={openRules} onClose={() => setOpenRules(false)} maxWidth="md" fullWidth scroll="paper" TransitionComponent={ZoomTransition}>
        <DialogTitle sx={{ m: 0, p: 2, fontWeight: 700 }}>
          Game Rules
          <IconButton aria-label="close" onClick={() => setOpenRules(false)} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers><GameRules /></DialogContent>
      </Dialog>

      {/* Feedback / Bug Report Dialog */}
      <FeedbackDialog
        open={openFeedback}
        onClose={() => setOpenFeedback(false)}
        roomCode={roomCode}
      />

      <CustomSnackbar message={systemMessage} setMessage={setSystemMessage} />
      <AlertDialog open={alertInfo.open} onClose={handleCloseAlert} title={alertInfo.title} content={alertInfo.content} type={alertInfo.type} onConfirm={alertInfo.onConfirm} />

      {/* Main content */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <AnimatePresence mode="wait">
          {!isInGame ? (
            <motion.div key="lobby" variants={pageVariants} initial="initial" animate="animate" exit="exit" style={{ flex: 1, display: "flex" }}>
              <LobbyArea socket={socket} showAlert={showAlert} onBack={() => setShowLanding(true)} />
            </motion.div>
          ) : (
            <motion.div key="game" variants={pageVariants} initial="initial" animate="animate" exit="exit" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
              <GameArea key={gameKey} roomCode={roomCode} player={player} roomState={roomState} socket={socket} setSystemMessage={setSystemMessage} showAlert={showAlert} />
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      {/* Game Completed Dialog — Results + Play Again */}
      <Dialog
        fullScreen
        open={gameCompleted}
        onClose={() => { }}
        TransitionComponent={SlideTransition}
        PaperProps={{ sx: { background: theme.palette.gradient.bg, color: theme.palette.text.primary, overflowY: "auto" } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100dvh", p: 3, pt: 5 }}>
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, delay: 0.2 }}>
            <EmojiEventsIcon sx={{ fontSize: 72, color: "#FFD700", mb: 1, animation: "float 2s ease-in-out infinite" }} />
          </motion.div>

          <Typography variant="h4" sx={{ fontWeight: 800, mb: 3 }}>Game Over!</Typography>

          {finalRoomInfo && (
            <Box sx={{ width: "100%", maxWidth: 500 }}>
              {/* Leaderboard */}
              <List sx={{ p: 0, mb: 3 }}>
                {finalRoomInfo.players.sort((a, b) => b.score - a.score).map((p, i) => (
                  <motion.div key={p.id} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.15 }}>
                    <ListItem
                      sx={{
                        mb: 1.5,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.background.paper, 0.6),
                        backdropFilter: "blur(8px)",
                        border: `1px solid ${i === 0 ? alpha("#FFD700", 0.3) : theme.palette.divider}`,
                        px: 2, py: 1.5,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 40, height: 40, mr: 2, fontWeight: 700,
                          bgcolor: medalColors[i] || theme.palette.primary.main,
                          color: i < 3 ? "#000" : "#fff",
                          fontSize: "1rem",
                        }}
                      >
                        {i + 1}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 700 }}>
                          {p.username}
                          {p.id === player?.id && (
                            <Box component="span" sx={{ ml: 0.5, fontSize: "0.7rem", opacity: 0.6 }}>(You)</Box>
                          )}
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: i === 0 ? "#FFD700" : theme.palette.text.primary }}>
                        {p.score}
                      </Typography>
                    </ListItem>
                  </motion.div>
                ))}
              </List>

              {/* Play Again section */}
              <Box
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: alpha(theme.palette.background.paper, 0.5),
                  backdropFilter: "blur(8px)",
                  p: 2.5,
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, textAlign: "center" }}>
                  Play Again?
                </Typography>

                {/* Per-player ready indicators */}
                <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
                  {(roomState?.players || finalRoomInfo.players).map((p) => (
                    <Box
                      key={p.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 2,
                        border: `1px solid ${p.rematchReady
                          ? alpha(theme.palette.success.main, 0.5)
                          : theme.palette.divider
                          }`,
                        backgroundColor: p.rematchReady
                          ? alpha(theme.palette.success.main, 0.1)
                          : "transparent",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: p.rematchReady
                            ? theme.palette.success.main
                            : theme.palette.text.disabled,
                          transition: "background-color 0.3s ease",
                        }}
                      />
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                        {p.username}
                        {p.id === player?.id && " (You)"}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Host: ready toggle + round picker + Play Again */}
                {player?.isHost ? (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                    {/* Host ready toggle — same as everyone else */}
                    <Button
                      variant={roomState?.players?.find((p) => p.playerId === player?.playerId)?.rematchReady ? "contained" : "outlined"}
                      color="success"
                      size="large"
                      onClick={() => socket.emit("readyForRematch", { roomCode })}
                      sx={{ px: 4 }}
                    >
                      {roomState?.players?.find((p) => p.playerId === player?.playerId)?.rematchReady
                        ? "Ready! (click to unready)"
                        : "Ready to Play Again"}
                    </Button>

                    {/* Round picker + Play Again — host only, enabled when all ready */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>Rounds:</Typography>
                      {[3, 5, 7].map((r) => (
                        <Button
                          key={r}
                          size="small"
                          variant={rematchRounds === r ? "contained" : "outlined"}
                          onClick={() => setRematchRounds(r)}
                          sx={{ minWidth: 40, px: 1.5, py: 0.25 }}
                        >
                          {r}
                        </Button>
                      ))}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        disabled={!(roomState?.players || finalRoomInfo.players).every((p) => p.rematchReady)}
                        onClick={() => socket.emit("startRematch", { roomCode, totalRounds: rematchRounds })}
                        sx={{ px: 3 }}
                      >
                        Play Again
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="large"
                        onClick={handleCloseDialog}
                        sx={{ px: 3 }}
                      >
                        End Game
                      </Button>
                    </Box>
                    {!(roomState?.players || finalRoomInfo.players).every((p) => p.rematchReady) && (
                      <Typography variant="caption" sx={{ opacity: 0.5 }}>Waiting for all players to ready up...</Typography>
                    )}
                  </Box>
                ) : (
                  /* Non-host: ready toggle + leave */
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                    <Button
                      variant={roomState?.players?.find((p) => p.playerId === player?.playerId)?.rematchReady ? "contained" : "outlined"}
                      color="success"
                      size="large"
                      onClick={() => socket.emit("readyForRematch", { roomCode })}
                      sx={{ px: 4 }}
                    >
                      {roomState?.players?.find((p) => p.playerId === player?.playerId)?.rematchReady
                        ? "Ready! (click to unready)"
                        : "Ready to Play Again"}
                    </Button>
                    <Button variant="text" color="error" onClick={handleCloseDialog}>
                      Leave Game
                    </Button>
                    <Typography variant="caption" sx={{ opacity: 0.5 }}>Waiting for host to start...</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Dialog>
    </Box>
  );
}

export default App;